'use client'

import type { MarketId } from '@morpho-org/blue-sdk'

import { secs } from '@repo/utils'
import { useChainId } from '@repo/web3'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import type { LiquidatableResult } from '@/lib/shared/types'

import { orpc } from '@/lib/orpc/client'

/**
 * Collapses an alt-tab burst into one request. `refetchOnWindowFocus` stays on because the on-chain
 * half of the verdict is re-read every request; this only bounds how often a focus event can trigger
 * that full uncached sweep.
 */
const LIQUIDATABLE_STALE_MS = secs(5).toMs()

/**
 * Hook to retrieve positions eligible for liquidation.
 *
 * The payload carries `updatedAt`/`truncated` alongside `positions` — see {@link LiquidatableResult}.
 * Both describe the cached candidate set, so an empty `positions` with a stale `updatedAt` means
 * "discovery is degraded", not "nothing is liquidatable".
 *
 * @param marketId - Optional market ID to filter positions from a specific market
 */
export function useLiquidations(marketId?: MarketId): UseQueryResult<LiquidatableResult> {
  const chainId = useChainId()

  const queryOptions = orpc.positions.liquidatable.queryOptions({
    input: { marketId },
    context: { chainId }
  })

  // chainId travels in oRPC context (not input), so it is NOT part of the
  // default queryKey. Append it so each chain has its own cache entry —
  // otherwise switching chains serves stale rows and skips the refetch.
  return useQuery({
    ...queryOptions,
    queryKey: [...queryOptions.queryKey, { chainId }],
    staleTime: LIQUIDATABLE_STALE_MS
  })
}
